
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { getEducationList, getWorkDetail, saveApi, updateApi } from '../api';
import CoverUploader from '../components-pc/CoverUploader.vue';
import HomeFooter from '../components-pc/HomeFooter.vue';
import PcPageLoading from '../components-pc/PcPageLoading.vue';
import VideoUploader from '../components-pc/VideoUploader.vue';

const baseUrl = 'cqcc';

interface AiForm {
  workName: string;
  authorName: string;
  teacherName: string;
  schoolName: string;
  grade: string;
  education: string;
  contact: string;
  intro: string;
  resultLink: string;
}

interface UploaderInstance extends Vue {
  hasFiles(): boolean;
  uploadToOss(): Promise<string[]>;
  clear(): void;
  setInitialFile(url: string): void;
}

interface EducationOption {
  label: string;
  value: string;
}

export default Vue.extend({
  name: 'AiSignupPc',
  components: {
    CoverUploader,
    HomeFooter,
    PcPageLoading,
    VideoUploader,
  },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      coverValidating: false,
      videoValidating: false,
      educationOptions: [] as EducationOption[],
      form: {
        workName: '',
        authorName: '',
        teacherName: '',
        schoolName: '',
        grade: '',
        education: '',
        contact: '',
        intro: '',
        resultLink: '',
      } as AiForm,
    };
  },
  mounted() {
    this.initPageData();
  },
  methods: {
    async initPageData() {
      this.pageLoading = true;
      try {
        await Promise.all([
          this.fetchEducationOptions(),
          this.fetchWorkDetail(),
        ]);
      } finally {
        this.pageLoading = false;
      }
    },
    goSignup() {
      this.$router.push(`/${baseUrl}/signup`).catch((err: Error) => err);
    },
    getDefaultForm(): AiForm {
      return {
        workName: '',
        authorName: '',
        teacherName: '',
        schoolName: '',
        grade: '',
        education: '',
        contact: '',
        intro: '',
        resultLink: '',
      };
    },
    getEditId(): string {
      const id = this.$route.query.id;
      return Array.isArray(id) ? String(id[0] || '') : String(id || '');
    },
    getDetailPayload(res: any): any {
      return (
        (res && res.data && res.data.data) || (res && res.data) || res || {}
      );
    },
    parseUrlList(value: any): string[] {
      if (Array.isArray(value)) {
        return value.filter(Boolean).map((item) => String(item));
      }
      if (!value) {
        return [];
      }
      try {
        const list = JSON.parse(value);
        return Array.isArray(list)
          ? list.filter(Boolean).map((item) => String(item))
          : [];
      } catch (err) {
        return [String(value)];
      }
    },
    async fetchWorkDetail() {
      const id = this.getEditId();
      if (!id) {
        return;
      }

      try {
        const res: any = await getWorkDetail({ id });
        const detail = this.getDetailPayload(res);
        this.form = {
          workName: detail.title || '',
          authorName: detail.name || '',
          teacherName: detail.teacher || '',
          schoolName: detail.school || '',
          grade: detail.class || '',
          education: detail.education || '',
          contact:
            detail.contact ||
            detail.contact_way ||
            detail.contactWay ||
            detail.phone ||
            detail.mobile ||
            '',
          intro: detail.introduction || '',
          resultLink: detail.exp_url || '',
        };
        this.$nextTick(() => {
          const coverUploader = this.$refs.coverUploader as UploaderInstance;
          const videoUploader = this.$refs.videoUploader as UploaderInstance;
          if (coverUploader) {
            coverUploader.setInitialFile(detail.explanation || '');
          }
          if (videoUploader) {
            videoUploader.setInitialFile(
              this.parseUrlList(detail.files)[0] || '',
            );
          }
        });
      } catch (err) {
        console.error('人工智能生成作品详情获取失败：', err);
        message.error('作品详情获取失败，请稍后重试');
      }
    },
    normalizeEducationOption(item: any): EducationOption | null {
      if (typeof item === 'string' || typeof item === 'number') {
        const value = String(item);
        return { label: value, value };
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const label =
        item.label ||
        item.name ||
        item.title ||
        item.education ||
        item.text ||
        item.value;
      const value = item.value || item.id || item.key || label;

      if (!label || value === undefined || value === null) {
        return null;
      }

      return {
        label: String(label),
        value: String(value),
      };
    },
    getEducationPayload(res: any): any[] {
      if (Array.isArray(res)) {
        return res;
      }

      if (Array.isArray(res && res.data)) {
        return res.data;
      }

      if (Array.isArray(res && res.list)) {
        return res.list;
      }

      if (Array.isArray(res && res.data && res.data.list)) {
        return res.data.list;
      }

      return [];
    },
    async fetchEducationOptions() {
      try {
        const res: any = await getEducationList();
        const options = this.getEducationPayload(res)
          .map((item) => this.normalizeEducationOption(item))
          .filter(Boolean) as EducationOption[];

        this.educationOptions = options;
      } catch (err) {
        console.error('学历列表获取失败：', err);
        message.error('学历列表获取失败，请稍后重试');
      }
    },
    isValidLink(link: string): boolean {
      return /^https?:\/\/.+/i.test(link);
    },
    isSaveSuccess(res: any): boolean {
      return Boolean(
        res &&
          (res.code === 200 ||
            res.status === 200 ||
            res.status === 1 ||
            res.success === true),
      );
    },
    getResponseMessage(res: any, fallback: string): string {
      return (res && (res.msg || res.message)) || fallback;
    },
    validateForm(): boolean {
      const requiredFields: Array<{ key: keyof AiForm; label: string }> = [
        { key: 'workName', label: '作品名称' },
        { key: 'authorName', label: '作者名字' },
        { key: 'teacherName', label: '指导教师' },
        { key: 'schoolName', label: '所属院校' },
        { key: 'grade', label: '年级' },
        { key: 'education', label: '学历' },
        { key: 'contact', label: '联系方式' },
        { key: 'intro', label: '作品简介' },
        { key: 'resultLink', label: '生成成果链接' },
      ];
      const emptyField = requiredFields.find((item) => !this.form[item.key]);

      if (emptyField) {
        message.error(`请填写${emptyField.label}`);
        return false;
      }

      if (!this.isValidLink(this.form.resultLink)) {
        message.error('请输入正确的H5链接');
        return false;
      }

      const coverUploader = this.$refs.coverUploader as UploaderInstance;
      if (!coverUploader || !coverUploader.hasFiles()) {
        message.error('请上传作品封面');
        return false;
      }

      const videoUploader = this.$refs.videoUploader as UploaderInstance;
      if (!videoUploader || !videoUploader.hasFiles()) {
        message.error('请上传视频');
        return false;
      }

      return true;
    },
    async submitForm() {
      if (
        this.submitting ||
        this.coverValidating ||
        this.videoValidating ||
        !this.validateForm()
      ) {
        return;
      }

      const coverUploader = this.$refs.coverUploader as UploaderInstance;
      const videoUploader = this.$refs.videoUploader as UploaderInstance;
      const hideLoading = message.loading('作品上传中，请稍候...', 0);
      this.submitting = true;

      try {
        const [coverUrls, videoUrls] = await Promise.all([
          coverUploader.uploadToOss(),
          videoUploader.uploadToOss(),
        ]);
        const submitParams = {
          workType: 5,
          title: this.form.workName,
          name: this.form.authorName,
          school: this.form.schoolName,
          introduction: this.form.intro,
          files: JSON.stringify(videoUrls),
          teacher: this.form.teacherName,
          class: this.form.grade,
          education: this.form.education,
          contact: this.form.contact,
          explanation: coverUrls[0] || '',
          exp_url: this.form.resultLink,
        };

        const id = this.getEditId();
        const res: any = id
          ? await updateApi({ ...submitParams, id })
          : await saveApi(submitParams);
        if (!this.isSaveSuccess(res)) {
          message.error(this.getResponseMessage(res, '提交失败，请稍后重试'));
          return;
        }

        message.success(
          this.getResponseMessage(res, id ? '修改成功' : '提交成功'),
        );
        this.form = this.getDefaultForm();
        coverUploader.clear();
        videoUploader.clear();
        this.$router.back();
      } catch (err) {
        console.error('人工智能生成作品提交失败：', err);
        message.error('提交失败，请稍后重试');
      } finally {
        hideLoading();
        this.submitting = false;
      }
    },
  },
});
